import {
  GetQuestionReviewTaskByIdDocument,
  OrderType,
  useSearchUsersByAppRoleFiltersLazyQuery,
  useUpdateTaskMutation,
} from '@exam-management/graphql';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Form,
  FormButton,
  FormSearchSelection,
  PersonOutlinedIcon,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import { useQuestionReviewTaskDetails } from '../../providers';

const ChangeReviewerFormSchema = z.object({
  reviewer: z.object(
    {
      label: z.string(),
      value: z.string(),
    },
    { message: 'กรุณาเลือก Reviewer' },
  ),
});

export type ChangeReviewerFormValues = z.infer<typeof ChangeReviewerFormSchema>;

export interface SelectedReviewer {
  id: string;
  name: string;
  dueDate: Date | null;
  description?: string | null;
  assigneeId: string;
}

export interface ChangeReviewerModalProps {
  open: boolean;
  reviewer: SelectedReviewer | null;
  excludedReviewerIds: string[];
  onClose: () => void;
}

export default function ChangeReviewerModal(props: Readonly<ChangeReviewerModalProps>) {
  const { open, reviewer, excludedReviewerIds, onClose } = props;

  const { showPromiseSnackbar } = useSnackbar();
  const { taskDetails } = useQuestionReviewTaskDetails();

  const formContext = useForm<ChangeReviewerFormValues>({
    resolver: zodResolver(ChangeReviewerFormSchema),
  });

  const [searchUser, { data: userListData, loading: isSearchingUserList }] =
    useSearchUsersByAppRoleFiltersLazyQuery();

  const [updateTask, { loading: isUpdatingReviewer }] = useUpdateTaskMutation({
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetQuestionReviewTaskByIdDocument,
        variables: {
          input: {
            taskId: taskDetails.id,
          },
        },
      },
    ],
  });

  const handleSearchUser = (inputValue: string | null) => {
    const [firstname, lastname] = inputValue?.trim().split(' ') ?? [];

    searchUser({
      variables: {
        appRoleNames: [],
        order: {
          type: OrderType.Asc,
          field: 'firstname',
        },
        paging: {
          currentPage: 0,
          pageSize: 100,
        },
        search: {
          firstname,
          lastname,
          email: '',
          status: '',
        },
      },
      fetchPolicy: 'network-only',
    });
  };

  const userOptions = useMemo(() => {
    if (isSearchingUserList || !userListData || !reviewer) return [];

    const { users } = userListData.UsBoSearchUsersWithAppRoleFilters;

    return users
      .filter((user) => !excludedReviewerIds.includes(user.id))
      .map((user) => ({
        label: `${user.firstname} ${user.lastname}`,
        value: user.id,
      }));
  }, [excludedReviewerIds, isSearchingUserList, reviewer, userListData]);

  useEffect(() => {
    if (!open || !reviewer) {
      formContext.reset();
      return;
    }

    formContext.reset();
  }, [formContext, open, reviewer]);

  const handleClose = () => {
    if (isUpdatingReviewer) return;

    formContext.reset();
    onClose();
  };

  const handleChangeReviewer = async (data: ChangeReviewerFormValues) => {
    if (!reviewer) return;

    if (!reviewer.dueDate) {
      throw new Error('Due date is required before changing reviewer');
    }

    await updateTask({
      variables: {
        updateTaskInput: {
          id: reviewer.id,
          name: taskDetails.name ?? '',
          assigneeId: data.reviewer.value,
          dueDate: reviewer.dueDate,
        },
      },
    });

    onClose();
  };

  const handleSubmit = (data: ChangeReviewerFormValues) => {
    showPromiseSnackbar({
      message: {
        success: 'Reviewer has been updated',
        error: 'Failed to update reviewer',
        pending: 'Updating reviewer...',
      },
      promise: () => handleChangeReviewer(data),
    });
  };

  return (
    <FormProvider {...formContext}>
      <Dialog
        open={open}
        onClose={handleClose}
        disableEscapeKeyDown={isUpdatingReviewer}
        maxWidth='sm'
        fullWidth
        slotProps={{
          paper: {
            component: Form,
            onSubmit: formContext.handleSubmit(handleSubmit),
          },
          transition: {
            onExited: () => formContext.reset(),
          },
        }}
      >
        <DialogTitle onClose={handleClose}>Change Reviewer</DialogTitle>
        <Divider />
        <DialogContent>
          <FormSearchSelection
            name='reviewer'
            label='Reviewer'
            mode='fetch-debounce'
            options={userOptions}
            placeholder='กรุณาเลือก Reviewer'
            loading={isSearchingUserList}
            required
            onSearch={handleSearchUser}
            sx={{ flex: 1 }}
            slotProps={{
              input: {
                startAdornment: (
                  <PersonOutlinedIcon
                    sx={(theme) => ({
                      fontSize: '20px',
                      mr: theme.spacing(2),
                      color: theme.palette.grey[500],
                    })}
                  />
                ),
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant='outlined'
            color='secondary'
            onClick={handleClose}
            disabled={isUpdatingReviewer}
          >
            Cancel
          </Button>
          <FormButton label='Change Reviewer' type='submit' loading={isUpdatingReviewer} />
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
